import { DeleteFilled, IssuesCloseOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Form, FormInstance, Input, Row, Space } from 'antd';
import { FormListFieldData } from 'antd/lib/form/FormList';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { isUri } from 'valid-url';
import { IGeneralSettings } from '../models/generalSettingsModel';
import { ServerValidationStatus } from '../utils/validationStatusEnum';
import { useConfigurationCtx } from './ConfigurationContext';
import { IGeneralSettingsForm } from './GeneralSettings';
import { ServerCredentialsForm } from './ServerCredentialsForm';
import { ServerInUseWarning, ServerProps } from './ServerInUseWarning';
import { UrlAlert } from './UrlAlert';

const StyledCard = styled(Card)`
  &&& {
    margin-top: 15px;
  }
  &&& .ant-card-body {
    padding: 0;
    margin: -1px 0 0 -1px;
  }
  &&& .ant-card-grid {
    width: 100%;
    padding: 12px;
  }
`;

export interface ServerConfigurationFormProps {
  field: FormListFieldData;
  remove: (key: number) => void;
  form: FormInstance<IGeneralSettingsForm>;
}

interface ServerConfigurationFormState {
  credentialMissing: boolean;
  urlValid: boolean;
  validating: boolean;
  usedServices: ServerProps[];
}

export const ServerConfigurationForm = observer(({ field, remove, form }: ServerConfigurationFormProps) => {
  const { generalSettings, mapConfiguration, testServerUrl } = useConfigurationCtx();
  const [t] = useTranslation('geosecma-settings');

  const [state, setState] = React.useReducer(
    (state: ServerConfigurationFormState, newState: Partial<ServerConfigurationFormState>) => ({
      ...state,
      ...newState
    }),
    {
      credentialMissing: false,
      urlValid: true,
      validating: false,
      usedServices: []
    }
  );

  React.useEffect(() => {
    const services = mapConfiguration.layersTree.filter(
      (l: any) => l.serverUrl === form.getFieldValue(['serverInfos', field.name, 'url'])
    );
    setState({ usedServices: services });
  }, [field.name, form, mapConfiguration.layersTree]);

  const validateUrl = (_: any, url: string) => {
    const isValid = isUri(url);
    setState({ urlValid: Boolean(isValid) });

    if (isValid) {
      return Promise.resolve();
    }

    return Promise.reject(Error(t('urlValidationError')));
  };

  const validateServer = async () => {
    const { url, username, password, token, loginType } = form.getFieldValue(['serverInfos', field.name]);
    let credentialsValid = true;

    switch (loginType) {
      case 'username/password':
        if (!password || !username || password === '*****') {
          form.setFields([
            {
              name: ['serverInfos', field.name, 'validationStatus'],
              value: ServerValidationStatus.invalidCredentials
            },
            { name: ['serverInfos', field.name, 'password'], value: '' }
          ]);

          setState({ credentialMissing: true });
          credentialsValid = false;
        }
        break;
      case 'token':
        if (!token || token === '*****') {
          form.setFields([
            {
              name: ['serverInfos', field.name, 'validationStatus'],
              value: ServerValidationStatus.invalidCredentials
            },
            { name: ['serverInfos', field.name, 'token'], value: '' }
          ]);

          setState({ credentialMissing: true });
          credentialsValid = false;
        }
        break;
      default:
        break;
    }

    if (isUri(url) && credentialsValid) {
      setState({ validating: true });
      try {
        switch (loginType) {
          case 'username/password':
            await testServerUrl({
              url: url,
              username: username,
              password: password,
              getServiceList: false
            });
            break;
          case 'token':
            await testServerUrl({
              url: url,
              token: token,
              getServiceList: false
            });
            break;
          default:
            await testServerUrl({
              url: url,
              getServiceList: false
            });
            break;
        }
        form.setFields([
          { name: ['serverInfos', field.name, 'validationStatus'], value: ServerValidationStatus.valid }
        ]);
        //form.setFields doesnt trigger onValuesChange, where we usually update the mobx state,
        //so we update from here when we validate
        const values = form.getFieldsValue(true);
        generalSettings.setValues(values);
      } catch (error) {
        const e = error as any;
        if (e.geosecmaCode === 1007) {
          form.setFields([
            {
              name: ['serverInfos', field.name, 'validationStatus'],
              value: password || token ? ServerValidationStatus.invalidCredentials : ServerValidationStatus.unauthorized
            }
          ]);
        } else {
          form.setFields([
            { name: ['serverInfos', field.name, 'validationStatus'], value: ServerValidationStatus.invalidServer }
          ]);
        }
      } finally {
        setState({ validating: false, urlValid: true });
      }
    } else {
      setState({ validating: false, urlValid: true });
    }
  };

  return (
    <StyledCard.Grid hoverable={false}>
      <Row gutter={16} align="middle">
        {state.usedServices.length ? (
          <Col span={24}>
            <Form.Item>
              <Alert showIcon type="warning" message={<ServerInUseWarning usedServices={state.usedServices} />}></Alert>
            </Form.Item>
          </Col>
        ) : null}
        <Col span={24}>
          <Form.Item
            {...field}
            label={t('sourceUrl')}
            name={[field.name, 'url']}
            fieldKey={[field.key, 'url']}
            hasFeedback
            rules={[{ required: true, message: t('urlRequired') }, { validator: validateUrl }]}
          >
            <Input
              data-testid={`sourceUrlInput${field.key}`}
              placeholder="https://host.domain.com/arcgis/rest/services"
              disabled={state.usedServices.length > 0}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues: IGeneralSettings, currentValues: IGeneralSettings) => {
              const currentServerInfo = currentValues.serverInfos?.[field.name];
              const prevServerInfo = prevValues.serverInfos?.[field.name];
              if (!currentServerInfo) {
                return false;
              }
              return prevServerInfo?.validationStatus !== currentServerInfo.validationStatus;
            }}
          >
            {({ getFieldValue }) => (
              <Form.Item {...field} name={[field.name, 'validationStatus']} fieldKey={[field.key, 'validationStatus']}>
                <UrlAlert validationStatus={getFieldValue(['serverInfos', field.name, 'validationStatus'])} />
              </Form.Item>
            )}
          </Form.Item>
        </Col>
        <Col span={24}>
          <ServerCredentialsForm field={field} disabled={state.validating} form={form} />
        </Col>
        <Col span={24}>
          <Space>
            <Button
              loading={state.validating}
              disabled={!state.urlValid}
              icon={<IssuesCloseOutlined />}
              onClick={validateServer}
              data-testid={`validateSourceButton${field.key}`}
            >
              {t('validate')}
            </Button>
            <Button
              disabled={state.validating || !state.urlValid || !!state.usedServices.length}
              data-testid={`removeSourceButton${field.key}`}
              onClick={() => remove(field.name)}
              icon={<DeleteFilled />}
            >
              {t('remove')}
            </Button>
          </Space>
        </Col>
      </Row>
    </StyledCard.Grid>
  );
});
